import { useState } from "react";
import { ArrowRightLeft, ChevronDown } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import { cn } from "@/lib/utils";
import { useItemTransfer } from "@/hooks/useItemTransfer";
import type { OpenConsumption, OpenConsumptionItem } from "@/hooks/useOpenConsumption";

interface TransferItemDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  sourceConsumption: OpenConsumption | null;
  destinations: OpenConsumption[];
  onSuccess: () => void;
}

export function TransferItemDialog({
  open,
  onOpenChange,
  sourceConsumption,
  destinations,
  onSuccess,
}: TransferItemDialogProps) {
  const [selectedItem, setSelectedItem] = useState<OpenConsumptionItem | null>(null);
  const [selectedDestination, setSelectedDestination] = useState<OpenConsumption | null>(null);
  const [quantity, setQuantity] = useState(1);
  const [destListOpen, setDestListOpen] = useState(false);
  
  const { transferItem, isTransferring } = useItemTransfer();

  const formatCurrency = (value: number) =>
    new Intl.NumberFormat("pt-BR", {
      style: "currency",
      currency: "BRL",
    }).format(value);

  const handleTransfer = async () => {
    if (!selectedItem || !selectedDestination || !sourceConsumption) return;

    const success = await transferItem({
      orderItemId: selectedItem.id,
      fromOrderId: sourceConsumption.orderIds[0],
      toOrderId: selectedDestination.orderIds[0],
      quantity,
    });

    if (success) {
      onOpenChange(false);
      setSelectedItem(null);
      setSelectedDestination(null);
      setQuantity(1);
      onSuccess();
    }
  };

  if (!sourceConsumption) return null;

  const filteredDestinations = destinations.filter(
    (d) => d.id !== sourceConsumption.id
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ArrowRightLeft className="h-5 w-5" />
            Transferir Item
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          <div className="p-3 bg-muted rounded-lg">
            <p className="text-sm text-muted-foreground">De:</p>
            <p className="font-medium">
              {sourceConsumption.type === "table"
                ? `Mesa ${sourceConsumption.number}`
                : `Comanda ${sourceConsumption.number}`}
            </p>
          </div>

          {/* Select Item */}
          <div className="space-y-2">
            <Label>Selecione o item</Label>
            <div className="space-y-1 max-h-[150px] overflow-y-auto border rounded-lg p-2">
              {sourceConsumption.items.map((item) => (
                <button
                  key={item.id}
                  type="button"
                  onClick={() => {
                    setSelectedItem(item);
                    setQuantity(Math.min(1, item.quantity));
                  }}
                  className={cn(
                    "w-full text-left p-2 rounded hover:bg-muted transition-colors",
                    selectedItem?.id === item.id && "bg-primary/10 border-primary"
                  )}
                >
                  <div className="flex justify-between">
                    <span className="font-medium">
                      {item.quantity}x {item.product_name}
                    </span>
                    <span className="text-muted-foreground">
                      {formatCurrency(item.total_price)}
                    </span>
                  </div>
                </button>
              ))}
            </div>
          </div>

          {/* Quantity */}
          {selectedItem && selectedItem.quantity > 1 && (
            <div className="space-y-2">
              <Label>Quantidade a transferir</Label>
              <Input
                type="number"
                min="1"
                max={selectedItem.quantity}
                value={quantity}
                onChange={(e) =>
                  setQuantity(
                    Math.min(
                      Math.max(1, parseInt(e.target.value) || 1),
                      selectedItem.quantity
                    )
                  )
                }
              />
              <p className="text-xs text-muted-foreground">
                Máximo: {selectedItem.quantity}
              </p>
            </div>
          )}

          {/* Select Destination */}
          <div className="space-y-2">
            <Label>Transferir para</Label>
            <Collapsible open={destListOpen} onOpenChange={setDestListOpen}>
              <CollapsibleTrigger asChild>
                <Button variant="outline" className="w-full justify-between">
                  {selectedDestination
                    ? selectedDestination.type === "table"
                      ? `Mesa ${selectedDestination.number}`
                      : `Comanda ${selectedDestination.number}`
                    : "Selecione o destino"}
                  <ChevronDown
                    className={cn(
                      "h-4 w-4 transition-transform",
                      destListOpen && "rotate-180"
                    )}
                  />
                </Button>
              </CollapsibleTrigger>
              <CollapsibleContent className="mt-2">
                <ScrollArea className="h-[150px] border rounded-lg p-2">
                  {filteredDestinations.length === 0 ? (
                    <p className="text-sm text-muted-foreground p-2">
                      Nenhum destino disponível
                    </p>
                  ) : (
                    <div className="space-y-1">
                      {filteredDestinations.map((dest) => (
                        <button
                          key={dest.id}
                          type="button"
                          onClick={() => {
                            setSelectedDestination(dest);
                            setDestListOpen(false);
                          }}
                          className={cn(
                            "w-full text-left p-2 rounded hover:bg-muted transition-colors",
                            selectedDestination?.id === dest.id && "bg-primary/10"
                          )}
                        >
                          <span className="font-medium">
                            {dest.type === "table"
                              ? `Mesa ${dest.number}`
                              : `Comanda ${dest.number}`}
                          </span>
                          {dest.customerName && (
                            <span className="text-muted-foreground ml-2">
                              ({dest.customerName})
                            </span>
                          )}
                        </button>
                      ))}
                    </div>
                  )}
                </ScrollArea>
              </CollapsibleContent>
            </Collapsible>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancelar
          </Button>
          <Button
            onClick={handleTransfer}
            disabled={!selectedItem || !selectedDestination || isTransferring}
          >
            {isTransferring ? "Transferindo..." : "Transferir"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
